import { DEFAULT_TEMPLATE_FORM } from './templateConstants';

const PHONE_REGEX = /^([1-9]{1}[0-9]{1,12})$/;

export const TEMPLATE_NAME_TAKEN_MESSAGE =
  'A template with this name already exists for this bot number.';

export const TEMPLATE_BODY_VARIABLE_PLACEMENT_MESSAGE =
  'Variables cannot be at the start or end of the body text.';

const BODY_VARIABLE_TOKEN_REGEX = /\{\{\d+\}\}/;

export function isValidWebsiteUrl(value = '') {
  const urlValue = `${value}`.trim();
  if (!urlValue) return false;

  try {
    const url = new URL(urlValue);
    return (
      (url.protocol === 'http:' || url.protocol === 'https:')
      && Boolean(url.hostname)
      // A dynamic variable cannot be the website's hostname, e.g. https://{{1}}.
      && !url.hostname.includes('{{')
      && !url.hostname.includes('}}')
    );
  } catch (error) {
    return false;
  }
}

export function isBodyVariablePlacementError(message) {
  if (!message || typeof message !== 'string') {
    return false;
  }
  return (
    message === TEMPLATE_BODY_VARIABLE_PLACEMENT_MESSAGE
    || message.startsWith('Variables cannot be at the start of the body text')
    || message.startsWith('Body text cannot end with variable')
  );
}

export function getBodyVariablePlacementError(bodyText = '') {
  const text = `${bodyText || ''}`.trim();
  if (!text || !BODY_VARIABLE_TOKEN_REGEX.test(text)) {
    return null;
  }

  const startMatch = text.match(/^\{\{(\d+)\}\}/);
  if (startMatch) {
    return `Variables cannot be at the start of the body text. Remove or move {{${startMatch[1]}}}.`;
  }

  const endMatch = text.match(/(\{\{\d+\}\})\s*$/);
  if (endMatch) {
    return `Body text cannot end with variable ${endMatch[1]}. Add text after the last variable.`;
  }

  return null;
}

export function validateTemplateForm(form, buttons = {}) {
  const errors = {};
  const data = form ?? DEFAULT_TEMPLATE_FORM;

  if (!data.name) errors.name = 'Template name is required';
  if (!data.language) errors.language = 'Language is required';

  const bodyObj = data.components?.find((c) => c?.type === 'BODY');
  if (!bodyObj?.text) {
    errors.body = 'This field is required';
  } else {
    const placementError = getBodyVariablePlacementError(bodyObj.text);
    if (placementError) {
      errors.body = placementError;
    }
  }

  bodyObj?.example?.body_text?.[0]?.forEach((item, index) => {
    if (!item) errors[`bodyVariable_${index}`] = 'This field is required';
  });

  data.param?.forEach((item, index) => {
    if (!item) errors[`bodyParams_${index}`] = 'This field is required';
  });

  const headerObj = data.components?.find((c) => c?.type === 'HEADER');
  if (headerObj?.format === 'TEXT' && !headerObj?.text) {
    errors.headerText = 'This field is required';
  }

  buttons.callAction?.forEach((btn, index) => {
    if (btn?.type === 'URL') {
      if (!btn.text) errors[`callActionBtn_text_${index}`] = 'This field is required';
      if (!btn.url) {
        errors[`callActionBtn_url_${index}`] = 'This field is required';
      } else if (!isValidWebsiteUrl(btn.url)) {
        errors[`callActionBtn_url_${index}`] = 'Please enter a valid website URL';
      }
    } else if (btn?.type === 'PHONE_NUMBER') {
      if (!btn.phone_number) errors[`callActionBtn_phone_number_${index}`] = 'Valid phone number is required';
      if (!btn.text) errors[`callActionBtn_text_${index}`] = 'This field is required';
    } else if (btn?.type === 'OFFER_CODE') {
      if (!btn.text) errors[`callActionBtn_text_${index}`] = 'This field is required';
    }
  });

  buttons.quickReply?.forEach((btn, index) => {
    if (!btn?.text) errors[`quickReplyBtn_${index}`] = 'This field is required';
  });

  return errors;
}

export function validateSendForm({
  phoneNumber,
  bodyParams,
  buttonUrlParams,
  buttonsWithExamples = [],
  headerComponent,
  headerFile,
  useCreateImage,
}) {
  const errors = {};

  if (!PHONE_REGEX.test(phoneNumber) || phoneNumber.length < 10) {
    errors.phone_number = 'Please enter a valid number with country code (min 10 digits)';
  }

  bodyParams?.forEach((item, index) => {
    if (!item?.text?.trim()) errors[`bodyParams_${index}`] = 'This field is required';
  });

  buttonsWithExamples.forEach((button) => {
    const index = button.originalIndex ?? 0;
    const value = buttonUrlParams?.[index];
    if (value == null || `${value}`.trim() === '') {
      errors[`buttonUrlParams_${index}`] = 'This field is required';
    }
  });

  if (headerComponent?.format !== 'TEXT' && headerComponent && !headerFile && !useCreateImage) {
    errors.headerFile = 'Please upload a header file or use the created media';
  }

  return errors;
}

export function hasValidationErrors(errors = {}) {
  return Object.keys(errors).length > 0;
}

export function slugifyTemplateName(value = '') {
  return value.replace(/ /g, '_').toLowerCase();
}
